import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Calendar, ListOrdered, Loader2, X } from 'lucide-react';
import { getReferences } from '../../api/contentApi';

/**
 * ReferenceSafetyDialog
 *
 * Shown before archive / trash / purge. Fetches /media/:id/references, groups
 * schedule + playlist rows, and requires a 3-second readthrough timer before
 * the destructive button enables — unless the backend returns 0 references,
 * in which case the timer is skipped.
 *
 * Props:
 *   open: boolean
 *   mode: 'archive' | 'trash' | 'purge'
 *   items: Array<{ contentId, title }>  — one or many (bulk)
 *   onCancel: () => void
 *   onConfirm: (items) => Promise<{ cascaded?: { schedule_slots?, playlist_items? } } | void>
 */
const MODE_COPY = {
  archive: {
    title: 'Archive media?',
    verb: 'archive',
    cta: 'Confirm — detach and archive',
    body: 'Archiving removes the asset from active schedules and playlists. You can unarchive later; references will NOT auto-reattach.',
  },
  trash: {
    title: 'Move to Trash?',
    verb: 'trash',
    cta: 'Confirm — detach and move to Trash',
    body: 'Trashing hides the asset from the library. It stops playing everywhere. Restore brings it back at the root folder — references do not auto-reattach.',
  },
  purge: {
    title: 'Delete forever?',
    verb: 'delete',
    cta: 'Confirm — permanently delete',
    body: 'This permanently removes the file from storage. The underlying S3 object is deleted. This cannot be undone.',
  },
};

const READTHROUGH_SECONDS = 3;

const overlayStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 9999,
  padding: '16px',
};

const boxStyle = {
  width: '100%',
  maxWidth: '520px',
  maxHeight: '80vh',
  backgroundColor: '#fff',
  borderRadius: '14px',
  boxShadow: '0 24px 60px rgba(15, 23, 42, 0.25)',
  display: 'flex',
  flexDirection: 'column',
  outline: 'none',
};

const headerStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  padding: '20px 20px 0 20px',
  gap: '12px',
};

const bodyStyle = {
  padding: '8px 20px 0 20px',
  overflowY: 'auto',
  flex: 1,
};

const footerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  gap: '10px',
  padding: '16px 20px 20px 20px',
  borderTop: '1px solid #f1f5f9',
  marginTop: '16px',
};

const baseBtn = {
  fontSize: '14px',
  fontWeight: 600,
  padding: '10px 16px',
  borderRadius: '10px',
  border: 'none',
  cursor: 'pointer',
};

const cancelBtn = {
  ...baseBtn,
  backgroundColor: '#f1f5f9',
  color: '#0f172a',
};

const dangerBtn = (enabled) => ({
  ...baseBtn,
  backgroundColor: enabled ? '#dc2626' : '#fecaca',
  color: enabled ? '#fff' : '#7f1d1d',
  cursor: enabled ? 'pointer' : 'not-allowed',
  minWidth: '220px',
});

const groupHeaderStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#334155',
  margin: '14px 0 6px 0',
};

const rowStyle = {
  fontSize: '13px',
  color: '#475569',
  padding: '6px 10px',
  backgroundColor: '#f8fafc',
  borderRadius: '8px',
  marginBottom: '4px',
};

const ReferenceSafetyDialog = ({ open, mode, items = [], projectId, onCancel, onConfirm }) => {
  const boxRef = useRef(null);
  const cancelBtnRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [references, setReferences] = useState(null); // { schedules, playlists, total } aggregated across items
  const [remaining, setRemaining] = useState(READTHROUGH_SECONDS);
  const [submitting, setSubmitting] = useState(false);

  const copy = MODE_COPY[mode] || MODE_COPY.archive;

  // Fetch references (union across items if bulk)
  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setReferences(null);
    const ids = items.map((i) => i?.contentId).filter(Boolean);
    if (ids.length === 0) {
      setReferences({ schedules: [], playlists: [], total: 0 });
      setLoading(false);
      return undefined;
    }
    Promise.all(ids.map((id) => getReferences(projectId, id).catch((err) => {
      console.error('getReferences failed for', id, err);
      return { schedules: [], playlists: [], total: 0, _err: err?.message || 'Failed' };
    })))
      .then((results) => {
        if (cancelled) return;
        const schedules = [];
        const playlists = [];
        let anyError = null;
        results.forEach((r, idx) => {
          if (r?._err) anyError = r._err;
          (r.schedules || []).forEach((s) => schedules.push({ ...s, _contentId: ids[idx] }));
          (r.playlists || []).forEach((p) => playlists.push({ ...p, _contentId: ids[idx] }));
        });
        setReferences({
          schedules,
          playlists,
          total: schedules.length + playlists.length,
        });
        if (anyError && !schedules.length && !playlists.length) {
          setError(anyError);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, items]);

  // Readthrough timer: starts when references load. If 0 refs, skip to 0.
  useEffect(() => {
    if (!open || loading) return undefined;
    if (!references) return undefined;
    if (references.total === 0) {
      setRemaining(0);
      return undefined;
    }
    setRemaining(READTHROUGH_SECONDS);
    const started = Date.now();
    const iv = window.setInterval(() => {
      const elapsed = Math.floor((Date.now() - started) / 1000);
      const left = Math.max(0, READTHROUGH_SECONDS - elapsed);
      setRemaining(left);
      if (left <= 0) window.clearInterval(iv);
    }, 200);
    return () => window.clearInterval(iv);
  }, [open, loading, references]);

  // Focus trap + Escape handler
  useEffect(() => {
    if (!open) return undefined;
    const prevActive = document.activeElement;
    // focus cancel by default (safer)
    window.setTimeout(() => {
      if (cancelBtnRef.current) cancelBtnRef.current.focus();
    }, 0);
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCancel?.();
      } else if (e.key === 'Tab' && boxRef.current) {
        const focusables = boxRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (prevActive && typeof prevActive.focus === 'function') {
        try { prevActive.focus(); } catch (_) { /* noop */ }
      }
    };
  }, [open, onCancel]);

  const canConfirm = useMemo(() => {
    if (submitting || loading) return false;
    if (!references) return false;
    return remaining <= 0;
  }, [submitting, loading, references, remaining]);

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      await onConfirm?.(items);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      style={overlayStyle}
      onClick={() => { if (!submitting) onCancel?.(); }}
      role="presentation"
    >
      <div
        ref={boxRef}
        style={boxStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ref-safety-title"
        aria-describedby="ref-safety-body"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '36px', height: '36px', borderRadius: '50%',
              backgroundColor: mode === 'purge' ? '#fee2e2' : '#fef3c7',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AlertTriangle size={20} color={mode === 'purge' ? '#dc2626' : '#d97706'} />
            </div>
            <h2 id="ref-safety-title" style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: '#0f172a' }}>
              {copy.title}
              {items.length > 1 && <span style={{ fontWeight: 500, color: '#64748b' }}> · {items.length} items</span>}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => { if (!submitting) onCancel?.(); }}
            aria-label="Close"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '4px' }}
          >
            <X size={18} />
          </button>
        </div>

        <div id="ref-safety-body" style={bodyStyle}>
          <p style={{ margin: '8px 0 4px 0', fontSize: '14px', color: '#475569', lineHeight: 1.5 }}>
            {copy.body}
          </p>

          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '18px 0', color: '#64748b', fontSize: '13px' }}>
              <Loader2 size={16} className="mv2-spin" /> Checking references…
            </div>
          )}

          {!loading && error && (
            <div style={{ margin: '10px 0', padding: '10px 12px', borderRadius: '10px', backgroundColor: '#fef2f2', color: '#991b1b', fontSize: '13px' }}>
              {error}
            </div>
          )}

          {!loading && references && references.total === 0 && (
            <div style={{ margin: '10px 0 0 0', padding: '10px 12px', borderRadius: '10px', backgroundColor: '#ecfdf5', color: '#065f46', fontSize: '13px' }}>
              No active schedules or playlists reference this media. Safe to {copy.verb}.
            </div>
          )}

          {!loading && references && references.schedules.length > 0 && (
            <>
              <div style={groupHeaderStyle}>
                <Calendar size={14} /> {references.schedules.length} schedule{references.schedules.length === 1 ? '' : 's'}
              </div>
              {references.schedules.map((s, idx) => (
                <div key={`s-${s._id || idx}`} style={rowStyle}>
                  {s.screen_id || 'Screen'} — {s.start_time || '??:??'}–{s.end_time || '??:??'}
                  {s.role ? <span style={{ marginLeft: '6px', color: '#64748b' }}>({s.role})</span> : null}
                </div>
              ))}
            </>
          )}

          {!loading && references && references.playlists.length > 0 && (
            <>
              <div style={groupHeaderStyle}>
                <ListOrdered size={14} /> {references.playlists.length} playlist{references.playlists.length === 1 ? '' : 's'}
              </div>
              {references.playlists.map((p, idx) => (
                <div key={`p-${p._id || idx}`} style={rowStyle}>
                  {p.name || p._id} {p.role ? <span style={{ color: '#64748b' }}>({p.role})</span> : null}
                </div>
              ))}
            </>
          )}
        </div>

        <div style={footerStyle}>
          <button
            ref={cancelBtnRef}
            type="button"
            style={cancelBtn}
            onClick={() => { if (!submitting) onCancel?.(); }}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            style={dangerBtn(canConfirm)}
            onClick={handleConfirm}
            disabled={!canConfirm}
          >
            {submitting
              ? 'Working…'
              : remaining > 0 && references && references.total > 0
                ? `${copy.cta} (${remaining}s)`
                : copy.cta}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReferenceSafetyDialog;
